import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Navbar from "../../components/layout/Navbar";
import Footer from "../../components/layout/Footer";
import {
  fetchBrandedVodCategories,
  fetchVodCategoryContent,
  getImageUrl,
} from "../../redux/content";
import "../../components/styles/brandPage.scss";

/**
 * BrandPage — Brand/Studio detail page (e.g. Netflix, HBO, Disney+, DC)
 * Figma: 1.1 Brand (Desktop) (24654:5088) — 1920×2442
 *
 * Data flow:
 *   1. Read brand uid from URL (`/brand/:name`)
 *   2. Resolve the matching VOD category flagged `branded:true`
 *   3. Fetch movies/series for that category id
 *   4. Render hero (background_image_id + heading_image_id logo + background_color)
 *   5. Render the content as a poster grid → /details/:movieId
 */

const BrandPage = () => {
  const { name: brandUid } = useParams();
  const navigate = useNavigate();
  const [category, setCategory] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      console.log("[BrandPage] Loading brand:", brandUid);
      setLoading(true);
      const cats = await fetchBrandedVodCategories();
      const decoded = decodeURIComponent(brandUid || "").toLowerCase();
      const match = cats.find((c) => (c.uid || "").toLowerCase() === decoded);
      if (cancelled) return;
      if (!match) {
        console.warn("[BrandPage] No branded category matched uid:", decoded);
        setCategory(null);
        setItems([]);
        setLoading(false);
        return;
      }
      console.log("[BrandPage] Matched category:", match.uid, "id:", match.id);
      setCategory(match);
      const content = await fetchVodCategoryContent(match.id);
      if (cancelled) return;
      setItems(content);
      setLoading(false);
    };
    load();
    return () => { cancelled = true; };
  }, [brandUid]);

  const heroBg = category?.background_image_id
    ? getImageUrl({ image_id: category.background_image_id })
    : null;
  const heroLogo = category?.heading_image_id
    ? getImageUrl({ image_id: category.heading_image_id })
    : null;
  const heroFill = category?.background_color || "#0a0a0a";

  const handlePosterClick = (item) => {
    console.log("[BrandPage] poster click:", item.id, item.title);
    navigate(`/details/${item.id}`);
  };

  return (
    <div className="brand-page">
      <Navbar activeTab="Discover" />

      {/* Hero Section */}
      <section
        className="brand-page__hero"
        style={{ backgroundColor: heroFill }}
      >
        <div className="brand-page__hero-bg">
          {heroBg && (
            <img
              src={heroBg}
              alt=""
              className="brand-page__hero-bg-img"
            />
          )}
          <div className="brand-page__hero-tint" />
          <div className="brand-page__hero-gradient-top" />
          <div className="brand-page__hero-gradient-bottom" />
        </div>

        <div className="brand-page__hero-content">
          {heroLogo && (
            <img
              src={heroLogo}
              alt={category?.name || brandUid}
              className="brand-page__hero-logo"
            />
          )}
        </div>
      </section>

      {/* Poster Grid */}
      <section className="brand-page__grid">
        {!loading && items.length === 0 && (
          <div style={{ color: "#a3a3a3", padding: "40px 0" }}>
            No titles available for this brand.
          </div>
        )}
        {items.map((item) => {
          const img = getImageUrl(item, "POSTER") || getImageUrl(item);
          return (
            <div
              key={item.id}
              className="brand-page__poster"
              onClick={() => handlePosterClick(item)}
              role="button"
              tabIndex={0}
            >
              {img ? (
                <img src={img} alt={item.title || ""} className="brand-page__poster-img" />
              ) : (
                <div
                  className="brand-page__poster-img"
                  style={{ background: "#222", display: "flex", alignItems: "center", justifyContent: "center", color: "#888" }}
                >
                  {item.title || "Untitled"}
                </div>
              )}
            </div>
          );
        })}
      </section>

      <Footer />
    </div>
  );
};

export default BrandPage;
